<div class="border rounded-xl overflow-hidden" data-testid="holds">
  <div class="flex justify-between items-center px-4 py-3 border-b bg-gray-50">
    <div>
      <p class="text-sm font-semibold text-gray-900">{{ config.holds.title }}</p>
      <p class="text-xs text-gray-500">{{ config.holds.subtitle }}</p>
    </div>

    <button *ngIf="canManage" vs-button size="sm" appearance="outline" (click)="holdDialog.openDialog()">
      <vs-icon name="plus" class="mr-1"></vs-icon> {{ config.holds.add }}
    </button>
  </div>

  <ng-container *ngIf="holds$ | async as holds">
    <p *ngIf="!holds.length" class="px-4 py-4 text-sm text-gray-500 text-center">{{ config.holds.empty }}</p>

    <div *ngFor="let hold of holds" class="flex gap-3 px-4 py-3 border-b last:border-b-0" data-testid="hold">
      <div class="flex flex-col gap-1 grow min-w-0">
        <div class="flex items-center gap-2 flex-wrap">
          <vs-badge [theme]="statusTheme(hold.status)">{{ config.holds.statuses[hold.status] }}</vs-badge>
          <vs-badge theme="default">{{ config.holds.types[hold.type] }}</vs-badge>
          <span class="text-sm text-gray-700 truncate">{{ chargeName(hold) }}</span>
        </div>

        <p class="text-sm text-gray-900">{{ hold.requestedEvidence }}</p>

        <p class="text-xs text-gray-500">
          {{ config.holds.owner }}: {{ hold.ownerName }} ({{ config.holds.ownerTypes[hold.ownerType] }}) ·
          <span [class.text-danger-600]="isOverdue(hold)" data-testid="hold-due">
            {{ isOverdue(hold) ? config.holds.overdue : config.holds.due }} {{ hold.dueDate | date: 'mediumDate' }}
          </span>
        </p>

        <p *ngIf="hold.completedAt" class="text-xs text-gray-500">
          {{ config.holds.completedBy }} {{ hold.completedBy?.displayName || config.holds.someone }} ·
          {{ hold.completedAt | date: 'medium' }}
          <span *ngIf="hold.completionNote">· {{ hold.completionNote }}</span>
        </p>
        <div
          *ngIf="hold.evidence?.length"
          class="flex flex-wrap items-center gap-1 min-w-0"
          data-testid="hold-evidence"
        >
          <span class="text-xs text-gray-500">{{ config.holds.evidence }}:</span>
          <ng-container *ngFor="let file of hold.evidence">
            <span
              *ngIf="file.removed; else evidenceFile"
              class="flex gap-1 max-w-full min-w-0 px-2 text-xs text-gray-400"
              [title]="file.title"
              data-testid="hold-evidence-removed"
            >
              <span class="truncate">{{ file.title }}</span>
              <span class="shrink-0">{{ config.holds.removedFile }}</span>
            </span>
            <ng-template #evidenceFile>
              <button
                type="button"
                class="inline-flex items-center gap-1.5 max-w-full min-w-0 rounded-full border border-gray-200 py-0.5 pl-0.5 pr-2.5 text-xs text-gray-700 hover:bg-gray-50"
                [title]="file.title"
                data-testid="hold-evidence-file"
                (click)="openEvidence(documentDialog, file)"
              >
                <ws-file-thumb [file]="filesById.get(file.storageItemId)" [size]="20" [round]="true"></ws-file-thumb>
                <span class="truncate">{{ file.title }}</span>
              </button>
            </ng-template>
          </ng-container>
        </div>
        <p *ngIf="hold.cancelledAt" class="text-xs text-gray-500">
          {{ config.holds.cancelledReason }} · {{ hold.cancelReason }}
        </p>
      </div>

      <div *ngIf="hold.status === open && (canClose || canAttach)" class="flex items-start gap-2 shrink-0">
        <button
          *ngIf="canAttach"
          vs-button
          size="sm"
          appearance="outline"
          data-testid="hold-attach"
          (click)="askToAttach(completeDialog, hold)"
        >
          <vs-icon name="attachment-02" class="mr-1"></vs-icon> {{ config.holds.attachEvidence }}
        </button>
        <ng-container *ngIf="canClose">
          <button vs-button size="sm" appearance="outline" (click)="askToComplete(completeDialog, hold)">
            {{ config.holds.complete }}
          </button>
          <button
            vs-button
            size="sm"
            appearance="clear"
            (click)="askToClose(noteDialog, { kind: 'cancelHold', id: hold.id })"
          >
            {{ config.holds.cancel }}
          </button>
        </ng-container>
      </div>
    </div>
  </ng-container>

  <div class="flex justify-between items-center px-4 py-3 border-t bg-gray-50">
    <p class="text-sm font-semibold text-gray-900">{{ config.escalations.title }}</p>

    <button *ngIf="canManage" vs-button size="sm" appearance="outline" (click)="escalationDialog.openDialog()">
      <vs-icon name="flag-01" class="mr-1"></vs-icon> {{ config.escalations.add }}
    </button>
  </div>

  <ng-container *ngIf="escalations$ | async as escalations">
    <p *ngIf="!escalations.length" class="px-4 py-4 text-sm text-gray-500 text-center">
      {{ config.escalations.empty }}
    </p>

    <div
      *ngFor="let escalation of escalations"
      class="flex gap-3 px-4 py-3 border-t first:border-t-0"
      data-testid="escalation"
    >
      <div class="flex flex-col gap-1 grow min-w-0">
        <div class="flex items-center gap-2">
          <vs-badge [theme]="escalation.resolvedAt ? 'success' : 'warn'">
            {{ escalation.resolvedAt ? config.escalations.resolved : config.escalations.open }}
          </vs-badge>
          <span class="text-sm text-gray-700">{{ config.escalations.to }} {{ escalation.ownerName }}</span>
        </div>
        <p class="text-sm text-gray-900">{{ escalation.reason }}</p>
        <p class="text-xs text-gray-500">
          {{ config.escalations.raisedBy }} {{ escalation.createdBy?.displayName || config.escalations.someone }} ·
          {{ escalation.created | date: 'medium' }}
        </p>
        <p *ngIf="escalation.resolvedAt" class="text-xs text-gray-500" data-testid="escalation-resolved">
          {{ config.escalations.resolvedBy }} {{ escalation.resolvedBy?.displayName || config.escalations.someone }} ·
          {{ escalation.resolvedAt | date: 'medium' }}
          <span *ngIf="escalation.resolutionNote">· {{ escalation.resolutionNote }}</span>
        </p>
      </div>

      <button
        *ngIf="canClose && !escalation.resolvedAt"
        vs-button
        size="sm"
        appearance="outline"
        class="shrink-0 self-start"
        (click)="askToClose(noteDialog, { kind: 'resolveEscalation', id: escalation.id })"
      >
        {{ config.escalations.resolve }}
      </button>
    </div>
  </ng-container>

  <ws-claim-item-hold-dialog
    #holdDialog
    [claim]="claim"
    [item]="item"
    [lines]="lines"
    (saved)="reload()"
  ></ws-claim-item-hold-dialog>
  <ws-claim-item-escalation-dialog
    #escalationDialog
    [claim]="claim"
    [item]="item"
    (saved)="reload()"
  ></ws-claim-item-escalation-dialog>
  <ws-claim-item-hold-complete-dialog
    #completeDialog
    [claim]="claim"
    [canViewFiles]="canViewFiles"
    [canUploadFiles]="canUploadFiles"
    (confirmed)="confirmEvidence($event)"
  ></ws-claim-item-hold-complete-dialog>
  <ws-claim-item-note-dialog #noteDialog (confirmed)="confirmClose($event)"></ws-claim-item-note-dialog>
  <ws-view-claim-document-dialog #documentDialog (updated)="refresh()"></ws-view-claim-document-dialog>
</div>
